// src/services/profile.service.js
import CryptoJS from 'crypto-js';
import { USER_ENDPOINTS, SUBSCRIPTION_ENDPOINTS, CONTENT_ENDPOINTS } from '../utils/constants';

const ENCRYPTION_KEY = 'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2';

/**
 * Encrypt data using AES
 */
export function encryptData(data) {
  return CryptoJS.AES.encrypt(
    typeof data === 'string' ? data : JSON.stringify(data), 
    ENCRYPTION_KEY
  ).toString();
}

/**
 * Decrypt AES encrypted data
 */
export function decryptData(ciphertext) {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
    
    // Try to parse as JSON, return as string if it fails
    try {
      return JSON.parse(decryptedText);
    } catch (e) {
      return decryptedText;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
}

/**
 * Get user credentials from session storage
 */
export function getUserCredentials() {
  const userDetails = sessionStorage.getItem('userDetails');
  if (!userDetails) return null;
  
  const userDetailsObj = JSON.parse(userDetails);
  const token = sessionStorage.getItem('userToken');
  
  return {
    email: userDetailsObj.Email,
    token
  };
}

/**
 * Fetch account payment history
 */
export async function fetchPaymentHistory(token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials || !credentials.token) {
      throw new Error('No authentication token found');
    }
    
    // Get user email from session storage
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    if (!userDetails || !userDetails.Email) {
      throw new Error('User email not found in session storage');
    }
    
    const email = userDetails.Email;

    const response = await fetch(SUBSCRIPTION_ENDPOINTS.PAYMENT_HISTORY, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${credentials.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch payment history');
    }

    const historyData = await response.json();
    return historyData;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    throw error;
  }
}

/**
 * Fetch account details
 */
export async function fetchAccountDetails(token, email = null) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials || !credentials.token) {
      throw new Error('No authentication token found');
    }
    
    // If email is not provided, try to get it from session storage
    if (!email) {
      const currentUserDetails = JSON.parse(sessionStorage.getItem('userDetails'));
      if (!currentUserDetails || !currentUserDetails.Email) {
        throw new Error('User email not found in session storage');
      }
      email = currentUserDetails.Email;
    }

    const response = await fetch(USER_ENDPOINTS.ACCOUNT_DETAILS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${credentials.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch account details');
    }

    const accountDetails = await response.json();
    
    if (!accountDetails || !accountDetails.user) {
      throw new Error('Invalid account details response structure');
    }
    
    return accountDetails;
  } catch (error) {
    console.error('Error fetching account details:', error);
    throw error;
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(formData, token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials || !credentials.token) {
      throw new Error('No authentication token found');
    }
    
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    
    const response = await fetch(USER_ENDPOINTS.EDIT_PROFILE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${credentials.token}`
      },
      body: JSON.stringify({
        Email: userDetails.Email,
        newData: formData
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update user profile');
    }
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

/**
 * Check payment status
 */
export async function checkPaymentStatus(token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    const response = await fetch(USER_ENDPOINTS.ACCOUNT_DETAILS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${credentials.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: credentials.email })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to check payment status: ${response.status}`);
    }
    
    const result = await response.json();
    return {
      isOverdue: result.isOverdue || false,
      dataDeletionDate: result.dataDeletionDate || null
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    return { isOverdue: false };
  }
}

/**
 * Fetch storage usage data
 */
export async function fetchStorageData(token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    const response = await fetch(CONTENT_ENDPOINTS.ALL_BUCKETS_SIZE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${credentials.token}`,
      },
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed');
      }
      throw new Error(`Storage data fetch failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data || !Array.isArray(data.bucketSizes)) {
      return { bucketSizes: [] };
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching storage data:', error);
    return { bucketSizes: [] };
  }
}

/**
 * Fetch subscription plans
 */
export async function fetchSubscriptionPlans(token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    const response = await fetch(SUBSCRIPTION_ENDPOINTS.GET_ALL_PLANS, {
      headers: {
        'Authorization': `Bearer ${credentials.token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch subscription plans');
    }
    
    const data = await response.json();
    if (data.success && Array.isArray(data.plans)) {
      return data.plans;
    } else {
      throw new Error('Invalid data structure received from API');
    }
  } catch (error) {
    console.error('Error fetching subscription plans:', error);
    throw error;
  }
}

/**
 * Calculate plan upgrade amount
 */
export async function calculatePlanUpgradeAmount(currentPlanId, newPlan, nextPayDate, token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    // First get all subscription plans
    let subscriptionPlans = JSON.parse(sessionStorage.getItem('subscriptionPlans'));
    if (!subscriptionPlans) {
      subscriptionPlans = await fetchSubscriptionPlans(credentials.token);
      sessionStorage.setItem('subscriptionPlans', JSON.stringify(subscriptionPlans));
    }
    
    // Find current plan details
    const currentPlan = subscriptionPlans.find(plan => plan.id === parseInt(currentPlanId));
    if (!currentPlan) {
      throw new Error('Current plan not found in subscription plans');
    }
    
    // Convert prices from strings to numbers
    const currentPlanPrice = parseFloat(currentPlan.Price);
    const newPlanPrice = parseFloat(newPlan.Price);
    
    // Convert dates and normalize to start of day
    const nextPaymentDate = new Date(nextPayDate);
    nextPaymentDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate remaining days in current subscription
    const totalDays = 365; // Annual subscription
    const daysUsed = Math.floor((today - new Date(nextPaymentDate).setFullYear(nextPaymentDate.getFullYear() - 1)) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, totalDays - daysUsed);
    
    // Calculate daily rates and credit
    const currentPlanDailyRate = currentPlanPrice / 365;
    const credit = currentPlanDailyRate * remainingDays;
    
    // Calculate final subsidized amount
    const subsidizedAmount = Math.max(0, newPlanPrice - credit);
    
    // Set new next pay date
    const newNextPayDate = new Date(today);
    newNextPayDate.setFullYear(today.getFullYear() + 1);
    
    return {
      originalAmount: newPlanPrice,
      subsidizedAmount: Math.round(subsidizedAmount * 100) / 100,
      discount: Math.round(credit * 100) / 100,
      newNextPayDate: newNextPayDate.toISOString(),
      calculationDetails: {
        totalDays,
        daysUsed,
        remainingDays,
        currentPlanDailyRate: Math.round(currentPlanDailyRate * 100) / 100,
        creditAmount: Math.round(credit * 100) / 100
      }
    };
  } catch (error) {
    console.error('Error calculating plan upgrade amount:', error);
    throw error;
  }
}

/**
 * Change password
 */
export async function changePassword(currentPassword, newPassword, otp, token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    if (!userDetails || !userDetails.Email) {
      throw new Error('User email not found');
    }
    
    // If OTP is provided, verify password change
    if (otp) {
      const response = await fetch(USER_ENDPOINTS.VERIFY_PASSWORD_CHANGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.token}`,
        },
        body: JSON.stringify({ 
          email: userDetails.Email, 
          newPassword, 
          otp 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to verify OTP and change password');
      }
      
      return await response.json();
    } 
    // Otherwise, initiate password change
    else {
      const response = await fetch(USER_ENDPOINTS.INITIATE_PASSWORD_CHANGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.token}`,
        },
        body: JSON.stringify({ 
          email: userDetails.Email, 
          currentPassword 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to initiate password change');
      }
      
      return await response.json();
    }
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
}

/**
 * Change email
 */
export async function changeEmail(newEmail, confirmEmail, otp, token) {
  try {
    const credentials = token ? { token } : getUserCredentials();
    if (!credentials) {
      throw new Error('No authentication information found');
    }
    
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    if (!userDetails || !userDetails.Email) {
      throw new Error('User email not found');
    }
    
    // If OTP is provided, verify email change
    if (otp) {
      const response = await fetch(USER_ENDPOINTS.CHANGE_EMAIL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.token}`,
        },
        body: JSON.stringify({ 
          newEmail, 
          otp, 
          currentEmail: userDetails.Email 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to verify OTP and change email');
      }
      
      return await response.json();
    } 
    // Otherwise, initiate email change
    else {
      if (newEmail !== confirmEmail) {
        throw new Error('Emails do not match');
      }
      
      const response = await fetch(USER_ENDPOINTS.INITIATE_EMAIL_CHANGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.token}`,
        },
        body: JSON.stringify({ 
          newEmail, 
          currentEmail: userDetails.Email 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to initiate email change');
      }
      
      return await response.json();
    }
  } catch (error) {
    console.error('Error changing email:', error);
    throw error;
  }
}

/**
 * Format bytes to human-readable format
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format date to local string
 */
export function formatDateTime(dateTime) {
  const date = new Date(dateTime);
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Upload user profile picture
 */
export const uploadProfilePicture = async (file, token) => {
  try {

    // Step 1: Delete the current profile image
    const deleteResponse = await fetch(USER_ENDPOINTS.DELETE_PROFILE_IMAGE, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!deleteResponse.ok) {
      const deleteError = await deleteResponse.json();
      throw new Error(deleteError.error || deleteError.message || 'Failed to delete existing profile image');
    }

    // Step 2: Upload the new profile image
    const formData = new FormData();
    formData.append('image', file);

    const uploadResponse = await fetch(USER_ENDPOINTS.UPLOAD_PROFILE_IMAGE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await uploadResponse.json();

    if (!uploadResponse.ok) {
      throw new Error(data.error || data.details || 'Upload failed');
    }

    const timestamp = Date.now();

    return {
      success: true,
      imageUrl: `/api/user/profile-image?t=${timestamp}`,
      version: timestamp,
      message: data.message || 'Profile picture updated successfully'
    };
  } catch (error) {
    console.error('Error in uploadProfilePicture:', error);
    return {
      success: false,
      error: error.message || 'Failed to upload profile picture'
    };
  }
};
